/**
 * SKI AXIS · /accelerator — Cohort 1 Programme Page (Light Editorial).
 *
 * Standalone, additive page describing the 12-week governance architecture
 * programme. Linked from the Header nav and homepage Three-Layer section.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import {
  Shield, Brain, Globe2, ArrowRight, CheckCircle2, X,
  Calendar, GraduationCap, Banknote,
} from 'lucide-react';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import useSEO from '@/hooks/useSEO';

const STYLES = `
.acc-root{background:#FAF7F0;color:#0D1B2A;min-height:100vh;font-family:'DM Sans',-apple-system,sans-serif;}
.acc-shell{max-width:1200px;margin:0 auto;padding:64px 32px;}
@media(max-width:680px){.acc-shell{padding:40px 22px;}}

/* HERO */
.acc-hero{padding:80px 0 64px;border-bottom:1px solid rgba(13,27,42,.08);}
.acc-eyebrow{font-size:11px;font-weight:700;letter-spacing:.32em;color:#C8A84B;text-transform:uppercase;display:flex;align-items:center;gap:14px;margin-bottom:22px;}
.acc-eyebrow::before{content:'';width:32px;height:1px;background:#C8A84B;}
.acc-h1{font-family:'Cormorant Display',serif;font-size:clamp(46px,6.5vw,80px);font-weight:700;line-height:1.0;letter-spacing:-.025em;margin:0 0 22px;color:#0D1B2A;max-width:1000px;}
.acc-h1 em{font-style:italic;color:#C8A84B;font-weight:600;}
.acc-sub{font-size:19px;line-height:1.5;color:#3D4A5C;max-width:760px;font-weight:300;margin:0;}

/* OVERVIEW BLOCK */
.acc-overview{background:linear-gradient(135deg,#0D1B2A 0%,#1A2F4A 100%);color:#FAF7F0;padding:64px 56px;border-left:4px solid #C8A84B;margin-top:64px;position:relative;overflow:hidden;}
.acc-overview::before{content:'';position:absolute;top:-60px;right:-60px;width:280px;height:280px;background:radial-gradient(circle,rgba(200,168,75,.15) 0%,transparent 70%);}
.acc-overview-eb{font-size:11px;font-weight:700;letter-spacing:.32em;color:#C8A84B;text-transform:uppercase;margin-bottom:16px;display:flex;align-items:center;gap:14px;}
.acc-overview-eb::before{content:'';width:32px;height:1px;background:#C8A84B;}
.acc-overview-h{font-family:'Cormorant Display',serif;font-size:clamp(28px,3.6vw,42px);font-weight:700;line-height:1.15;letter-spacing:-.012em;margin:0 0 18px;color:#FAF7F0;}
.acc-overview-h em{font-style:italic;color:#C8A84B;}
.acc-overview-p{font-size:16px;line-height:1.7;color:rgba(250,247,240,.82);font-weight:300;margin:0;}

/* SECTION HEADERS */
.acc-section-h{font-family:'Cormorant Display',serif;font-size:clamp(36px,4.5vw,52px);font-weight:700;line-height:1.05;letter-spacing:-.018em;margin:0 0 14px;color:#0D1B2A;}
.acc-section-h em{font-style:italic;color:#C8A84B;}
.acc-section-sub{font-size:15px;color:#3D4A5C;margin:0 0 36px;max-width:720px;line-height:1.55;}

/* TIMELINE */
.acc-timeline{margin-top:80px;}
.acc-tl-grid{display:grid;grid-template-columns:1fr;gap:14px;}
.acc-tl-row{background:#fff;border:1px solid #E8DFC9;border-left:3px solid #C8A84B;padding:24px 28px;display:grid;grid-template-columns:140px 1fr;gap:22px;align-items:center;transition:transform .15s,box-shadow .15s;}
.acc-tl-row:hover{transform:translateX(4px);box-shadow:0 8px 32px rgba(13,27,42,.06);}
@media(max-width:680px){.acc-tl-row{grid-template-columns:1fr;gap:8px;padding:20px 22px;}}
.acc-tl-week{font-family:'Cormorant Display',serif;font-style:italic;font-weight:600;font-size:18px;color:#C8A84B;letter-spacing:.04em;}
.acc-tl-title{font-family:'Cormorant Display',serif;font-size:22px;font-weight:700;color:#0D1B2A;line-height:1.3;letter-spacing:-.005em;margin:0;}

/* CHECKLIST */
.acc-grad{margin-top:96px;background:#fff;border:1px solid #E8DFC9;padding:48px 56px;}
@media(max-width:680px){.acc-grad{padding:32px 24px;}}
.acc-grad-list{display:grid;grid-template-columns:repeat(2,1fr);gap:16px 28px;margin:0;padding:0;list-style:none;}
@media(max-width:680px){.acc-grad-list{grid-template-columns:1fr;}}
.acc-grad-item{display:flex;align-items:flex-start;gap:12px;font-size:14.5px;color:#0D1B2A;line-height:1.55;letter-spacing:.005em;}
.acc-grad-item .ck{color:#C8A84B;flex-shrink:0;margin-top:3px;}

/* FEE */
.acc-fee{margin-top:96px;display:grid;grid-template-columns:1.5fr 1fr;gap:32px;align-items:stretch;}
@media(max-width:880px){.acc-fee{grid-template-columns:1fr;}}
.acc-fee-main{background:linear-gradient(135deg,#0D1B2A 0%,#1A2F4A 100%);color:#FAF7F0;padding:56px 48px;border-left:4px solid #C8A84B;}
.acc-fee-eb{font-size:11px;font-weight:700;letter-spacing:.32em;color:#C8A84B;text-transform:uppercase;margin-bottom:18px;}
.acc-fee-amt{font-family:'Cormorant Display',serif;font-size:64px;font-weight:700;line-height:1;letter-spacing:-.02em;color:#FAF7F0;margin:0 0 6px;}
.acc-fee-amt em{font-style:italic;color:#C8A84B;font-size:38px;font-weight:600;}
.acc-fee-pp{font-size:13px;color:rgba(250,247,240,.6);letter-spacing:.04em;margin-bottom:22px;}
.acc-fee-list{margin:0;padding:0;list-style:none;}
.acc-fee-list li{font-size:13.5px;color:rgba(250,247,240,.82);padding:9px 0;border-top:1px solid rgba(200,168,75,.16);display:flex;align-items:center;gap:10px;}
.acc-fee-list li:first-child{border:0;}
.acc-fee-list li svg{color:#C8A84B;flex-shrink:0;}
.acc-fee-pay{margin-top:24px;font-size:13px;color:rgba(250,247,240,.7);font-style:italic;font-family:'Cormorant Display',serif;font-size:16px;border-top:1px solid rgba(200,168,75,.2);padding-top:18px;}

.acc-schol-card{background:#fff;border:1px solid #E8DFC9;border-top:4px solid #C8A84B;padding:48px 36px;display:flex;flex-direction:column;justify-content:space-between;}
.acc-schol-eb{font-size:10.5px;font-weight:700;letter-spacing:.28em;color:#C8A84B;text-transform:uppercase;margin-bottom:14px;}
.acc-schol-h{font-family:'Cormorant Display',serif;font-size:30px;font-weight:700;line-height:1.1;color:#0D1B2A;letter-spacing:-.012em;margin:0 0 14px;}
.acc-schol-h em{font-style:italic;color:#C8A84B;}
.acc-schol-p{font-size:13.5px;color:#3D4A5C;line-height:1.6;margin:0 0 22px;}
.acc-schol-cta{display:inline-flex;align-items:center;gap:8px;color:#0D1B2A;text-decoration:none;font-size:11px;font-weight:800;letter-spacing:.18em;text-transform:uppercase;border-bottom:1px solid #C8A84B;padding-bottom:6px;width:fit-content;transition:gap .15s;}
.acc-schol-cta:hover{gap:14px;color:#C8A84B;}

/* WHO SHOULD APPLY */
.acc-who{margin-top:96px;display:grid;grid-template-columns:1fr 1fr;gap:32px;}
@media(max-width:760px){.acc-who{grid-template-columns:1fr;}}
.acc-who-col h4{font-family:'Cormorant Display',serif;font-size:24px;font-weight:700;color:#0D1B2A;letter-spacing:-.005em;margin:0 0 18px;}
.acc-who-col h4 em{font-style:italic;color:#C8A84B;}
.acc-who-col ul{margin:0;padding:0;list-style:none;}
.acc-who-col li{display:flex;align-items:flex-start;gap:12px;padding:11px 0;border-bottom:1px solid #E8DFC9;font-size:14px;color:#0D1B2A;line-height:1.5;}
.acc-who-col li:last-child{border:0;}
.acc-who-col li svg{flex-shrink:0;margin-top:3px;}
.acc-who-yes svg{color:#C8A84B;}
.acc-who-no svg{color:#ad6240;}
.acc-who-no li{color:#7A8190;}

/* CTA STRIP */
.acc-cta{margin-top:96px;background:#0D1B2A;color:#FAF7F0;padding:64px 56px;text-align:center;border-top:3px solid #C8A84B;border-bottom:3px solid #C8A84B;}
@media(max-width:680px){.acc-cta{padding:48px 24px;}}
.acc-cta h3{font-family:'Cormorant Display',serif;font-size:clamp(28px,4vw,42px);font-weight:700;line-height:1.1;margin:0 0 14px;color:#FAF7F0;letter-spacing:-.012em;}
.acc-cta h3 em{font-style:italic;color:#C8A84B;}
.acc-cta p{font-size:15px;color:rgba(250,247,240,.7);max-width:640px;margin:0 auto 28px;line-height:1.6;font-weight:300;}
.acc-cta-btn{display:inline-flex;align-items:center;gap:10px;background:#C8A84B;color:#0D1B2A;padding:18px 36px;font-size:13px;font-weight:800;letter-spacing:.18em;text-transform:uppercase;text-decoration:none;transition:all .2s;}
.acc-cta-btn:hover{background:#FAF7F0;transform:translateY(-2px);}
`;

const TIMELINE = [
  { week: 'Week 1–2', title: 'Governance Audit & Entity Architecture' },
  { week: 'Week 3–4', title: 'AML/KYC & Financial Controls Framework' },
  { week: 'Week 5–6', title: 'IP Registration & Digital Asset Structure' },
  { week: 'Week 7–8', title: 'ESG/SDG Alignment & Reporting Framework' },
  { week: 'Week 9–10', title: 'Investor Readiness & Cross-Border Compliance' },
  { week: 'Week 11', title: 'Sentinel Integration & Continuous Governance Setup' },
  { week: 'Week 12', title: 'Demo Day Preparation & Investor Forum' },
];

const GRADUATE_RECEIVES = [
  'Fully built three-layer governance architecture',
  'Free UAE incorporation consultancy via IFZA (advisory only · licence & visa fees not included)',
  'AI governance assessment report (Sentinel powered)',
  'Access to SKI Axis investor and mentor network',
  'Demo Day seat at Invest UAE Forum',
  'SKI Axis Alumni Circle — lifetime access',
  'Microsoft, Google & AWS credits (via partner programmes)',
  'Dedicated profile page on skiaxis.com/founders',
];

const FEE_INCLUDES = [
  'Full 12-week programme + governance framework build',
  'IFZA incorporation consultancy (advisory only · government licence & visa fees payable separately)',
  'Investor & family-office network access',
  'Demo Day at Invest UAE Forum',
  'SKI Axis Alumni Circle (lifetime)',
];

const WHO_YES = [
  'Founders entering the UAE market for the first time',
  'International founders seeking UAE institutional investors',
  'Pre-seed and seed-stage founders building in AI, fintech, governance tech, deep tech',
  'Founders who have been told "come back when you have governance in place"',
];
const WHO_NO = [
  'Founders who have already raised Series A or above',
  'Service businesses without a scalable technology component',
];

export default function AcceleratorPage() {
  useSEO({
    title: 'The SKI Axis Accelerator Programme · Cohort 1 July 2026 · Dubai',
    description: 'SKI Axis is the UAE\'s first governance-first startup accelerator. 12 weeks. 20 founders. Three-layer governance framework. AED 6,900 fee with 5 fully-funded scholarships per cohort. Cohort 1 launches July 2026.',
  });

  return (
    <div className="acc-root" data-testid="accelerator-page">
      <style>{STYLES}</style>
      <Header />

      <div className="acc-shell">
        {/* HERO */}
        <div className="acc-hero">
          <div className="acc-eyebrow">SKI Axis · Cohort 1 · July 2026</div>
          <h1 className="acc-h1">The SKI Axis <em>Accelerator</em> Programme</h1>
          <p className="acc-sub">12 weeks. 20 founders. One governance framework that changes everything.</p>
        </div>

        {/* OVERVIEW */}
        <div className="acc-overview" data-testid="accelerator-overview">
          <div className="acc-overview-eb">Programme Overview</div>
          <h2 className="acc-overview-h">SKI Axis is not a pitching programme. It is a <em>governance architecture</em> programme.</h2>
          <p className="acc-overview-p">
            Every founder who completes SKI Axis leaves with the institutional infrastructure that sovereign investors,
            family offices, and cross-border capital providers require before they deploy capital. Twelve weeks. Three layers.
            One unforgettable cohort.
          </p>
        </div>

        {/* TIMELINE */}
        <div className="acc-timeline" data-testid="accelerator-timeline">
          <h3 className="acc-section-h">12-week sprint <em>timeline</em></h3>
          <p className="acc-section-sub">Each fortnight closes with a tangible deliverable — the architecture compounds week by week.</p>
          <div className="acc-tl-grid">
            {TIMELINE.map((row, i) => (
              <div key={i} className="acc-tl-row" data-testid={`acc-week-${i + 1}`}>
                <div className="acc-tl-week">{row.week}</div>
                <h4 className="acc-tl-title">{row.title}</h4>
              </div>
            ))}
          </div>
        </div>

        {/* WHAT GRADUATES RECEIVE */}
        <div className="acc-grad" data-testid="accelerator-deliverables">
          <h3 className="acc-section-h">What graduates <em>receive</em></h3>
          <p className="acc-section-sub">A complete institutional package — not a slide deck, not a certificate. Real, deployable infrastructure.</p>
          <ul className="acc-grad-list">
            {GRADUATE_RECEIVES.map((item, i) => (
              <li key={i} className="acc-grad-item">
                <CheckCircle2 className="ck h-5 w-5" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* FEE */}
        <div className="acc-fee" data-testid="accelerator-fee">
          <div className="acc-fee-main">
            <div className="acc-fee-eb">Investment in your foundation</div>
            <div className="acc-fee-amt"><em>AED</em> 6,500</div>
            <div className="acc-fee-pp">Per founder · Cohort 1 standard</div>
            <ul className="acc-fee-list">
              {FEE_INCLUDES.map((item, i) => (
                <li key={i}><CheckCircle2 className="h-4 w-4" /><span>{item}</span></li>
              ))}
            </ul>
            <div className="acc-fee-pay">
              Payment is flexible — 50% on acceptance, 50% at Week 6.
            </div>
          </div>

          <div className="acc-schol-card">
            <div>
              <div className="acc-schol-eb">She Built This × SKI Axis</div>
              <h3 className="acc-schol-h">5 fully-funded <em>scholarships</em> per cohort.</h3>
              <p className="acc-schol-p">
                Five seats per cohort are <strong>fully funded</strong> for women founders building governance-first ventures into the UAE.
                Apply through the standard form and tick the scholarship checkbox in Section 5.
              </p>
            </div>
            <Link to="/apply" className="acc-schol-cta" data-testid="acc-scholarship-cta">
              Apply with scholarship <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>

        {/* WHO SHOULD APPLY */}
        <div className="acc-who" data-testid="accelerator-who">
          <div className="acc-who-col acc-who-yes">
            <h4>This <em>is</em> for you if you are…</h4>
            <ul>
              {WHO_YES.map((item, i) => (
                <li key={i}><CheckCircle2 className="h-5 w-5" /><span>{item}</span></li>
              ))}
            </ul>
          </div>
          <div className="acc-who-col acc-who-no">
            <h4>This is <em>not</em> for you if you are…</h4>
            <ul>
              {WHO_NO.map((item, i) => (
                <li key={i}><X className="h-5 w-5" /><span>{item}</span></li>
              ))}
            </ul>
          </div>
        </div>

        {/* CTA STRIP */}
        <div className="acc-cta" data-testid="accelerator-cta-strip">
          <h3>Apply for <em>Cohort 1</em> — July 2026.</h3>
          <p>20 seats. 12-week programme. Applications close 30 June 2026. Reviewed on a rolling basis.</p>
          <Link to="/apply" className="acc-cta-btn" data-testid="acc-apply-cta">
            Apply Now <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>

      <Footer />
    </div>
  );
}
